import React, { useState } from 'react';
import type { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import {
  InformationCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import GradientCard from '../gamification/GradientCard';
import PasswordField from '../common/PasswordField';
import { useChangePassword } from '../../services/settings';
import {
  PASSWORD_HELP,
  computePasswordStrength,
  validateNewPassword,
} from '../../utils/passwordStrength';

/** Map a failed change-password request to a user-friendly message. */
const errorMessageFor = (err: unknown): string => {
  const axiosError = err as AxiosError<{ message?: string; error?: string }>;
  const status = axiosError.response?.status;
  if (status === 401) return 'Current password is incorrect';
  if (status === 403) {
    return 'Your account uses single sign-on. Manage your password with your provider.';
  }
  return (
    axiosError.response?.data?.message ||
    axiosError.response?.data?.error ||
    'Could not change your password. Please try again.'
  );
};

/**
 * Password-change card for email/password accounts. Mirrors the Figma
 * "Account/PasswordForm" reference: a soft gradient card with three outlined
 * password fields, an inline strength/match helper, and a primary action.
 */
const ChangePasswordForm: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const changePassword = useChangePassword();

  const strength = computePasswordStrength(newPassword);
  const { valid: newPasswordValid } = validateNewPassword(newPassword, confirmPassword);
  const isFormValid = currentPassword.length > 0 && newPasswordValid;
  const confirmMismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid || changePassword.isPending) return;

    try {
      const result = await changePassword.mutateAsync({
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      toast.success(result.message || 'Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(errorMessageFor(err));
    }
  };

  return (
    <GradientCard className="p-6" overlayOpacity="opacity-20 dark:opacity-25">
      <form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate>
        <PasswordField
          label="Current password"
          value={currentPassword}
          onChange={setCurrentPassword}
          autoComplete="current-password"
          required
          disabled={changePassword.isPending}
        />

        <PasswordField
          label="New password"
          value={newPassword}
          onChange={setNewPassword}
          autoComplete="new-password"
          required
          disabled={changePassword.isPending}
        >
          <div className="space-y-1.5">
            {newPassword && (
              <div className="flex items-center gap-2">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-700">
                  <div
                    className={`h-full rounded-full transition-[width] duration-300 ${strength.barClass}`}
                    style={{ width: `${(strength.score / 4) * 100}%` }}
                  />
                </div>
                <span className="text-xs font-medium text-text-secondary">{strength.label}</span>
              </div>
            )}
            <p className="flex items-center gap-1.5 text-xs text-text-secondary">
              <InformationCircleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
              {PASSWORD_HELP}
            </p>
          </div>
        </PasswordField>

        <PasswordField
          label="Confirm new password"
          value={confirmPassword}
          onChange={setConfirmPassword}
          autoComplete="new-password"
          required
          disabled={changePassword.isPending}
          error={confirmMismatch}
        >
          {confirmPassword &&
            (newPassword === confirmPassword ? (
              <p className="flex items-center gap-1.5 text-xs text-success-600">
                <CheckCircleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                Passwords match
              </p>
            ) : (
              <p className="flex items-center gap-1.5 text-xs text-error-600">
                <XCircleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                Passwords do not match
              </p>
            ))}
        </PasswordField>

        <div className="flex justify-end">
          <button type="submit" disabled={!isFormValid || changePassword.isPending} className="btn-primary">
            {changePassword.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </GradientCard>
  );
};

export default ChangePasswordForm;
